import { useEffect, useState } from "react";
import api, { money, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { escrowLabel } from "@/components/EscrowTimeline";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Plus, Truck } from "lucide-react";

const CATEGORIES = ["Painting", "Photography", "Sculpture", "Digital", "Print", "Mixed Media"];
const SAMPLE_IMAGES = [
  "https://images.unsplash.com/photo-1531913764164-f85c52e6e654?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1536924940846-227afb31e2a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1552160793-cbaf5be2f7bf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
];

const empty = { title: "", description: "", medium: "", dimensions: "", year: "", origin: "", category: "Painting", price: "", image_url: SAMPLE_IMAGES[0] };

export default function ArtistDashboard() {
  const { user } = useAuth();
  const [works, setWorks] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [shipping, setShipping] = useState(null);
  const [tracking, setTracking] = useState("");

  const load = () => Promise.all([
    api.get("/artworks", { params: { artist_id: user.user_id } }).then((r) => setWorks(r.data)),
    api.get("/orders/sales").then((r) => setSales(r.data)),
  ]).finally(() => setLoading(false));
  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/artworks", { ...form, price: parseFloat(form.price), images: [form.image_url] });
      toast.success("Artwork listed");
      setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const ship = async (id) => {
    if (!tracking) { toast.error("Enter a tracking number"); return; }
    try {
      await api.post(`/orders/${id}/ship`, { tracking_number: tracking, carrier: "DHL Express" });
      toast.success("Marked as shipped");
      setShipping(null); setTracking(""); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const payout = sales.filter((s) => s.escrow_status === "released").reduce((a, s) => a + s.artist_payout, 0);
  const pending = sales.filter((s) => ["funds_held", "shipped", "delivered"].includes(s.escrow_status)).reduce((a, s) => a + s.artist_payout, 0);

  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="overline-badge">Artist Studio</div>
            <h1 className="font-serif text-4xl mt-2">{user?.name}</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="btn-gold px-6 py-3 rounded-full text-sm font-semibold flex items-center gap-2" data-testid="add-artwork-btn">
                <Plus className="w-4 h-4" /> List new artwork
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-serif text-2xl">List a new artwork</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <input required placeholder="Title" value={form.title} onChange={(e) => set("title", e.target.value)}
                  data-testid="artwork-title-input" className="w-full rounded-lg border border-[var(--border-subtle)] px-4 py-2.5 text-sm" />
                <textarea required placeholder="Description" value={form.description} onChange={(e) => set("description", e.target.value)}
                  rows={3} data-testid="artwork-desc-input" className="w-full rounded-lg border border-[var(--border-subtle)] px-4 py-2.5 text-sm" />
                <div className="grid grid-cols-2 gap-3">
                  <input required placeholder="Medium (e.g. Oil on canvas)" value={form.medium} onChange={(e) => set("medium", e.target.value)}
                    className="rounded-lg border border-[var(--border-subtle)] px-4 py-2.5 text-sm" />
                  <input required placeholder="Dimensions" value={form.dimensions} onChange={(e) => set("dimensions", e.target.value)}
                    className="rounded-lg border border-[var(--border-subtle)] px-4 py-2.5 text-sm" />
                  <input placeholder="Year" value={form.year} onChange={(e) => set("year", e.target.value)}
                    className="rounded-lg border border-[var(--border-subtle)] px-4 py-2.5 text-sm" />
                  <input placeholder="Origin" value={form.origin} onChange={(e) => set("origin", e.target.value)}
                    className="rounded-lg border border-[var(--border-subtle)] px-4 py-2.5 text-sm" />
                  <select value={form.category} onChange={(e) => set("category", e.target.value)}
                    className="rounded-lg border border-[var(--border-subtle)] px-4 py-2.5 text-sm bg-white">
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                  <input required type="number" min="1" placeholder="Price (€)" value={form.price} onChange={(e) => set("price", e.target.value)}
                    data-testid="artwork-price-input" className="rounded-lg border border-[var(--border-subtle)] px-4 py-2.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[var(--text-muted)]">Artwork image</label>
                  <input required placeholder="Image URL" value={form.image_url} onChange={(e) => set("image_url", e.target.value)}
                    className="w-full mt-1 rounded-lg border border-[var(--border-subtle)] px-4 py-2.5 text-sm" />
                  <div className="flex gap-2 mt-2">
                    {SAMPLE_IMAGES.map((s) => (
                      <img key={s} src={s} alt="" onClick={() => set("image_url", s)}
                        className={`w-14 h-14 object-cover rounded cursor-pointer border-2 ${form.image_url === s ? "border-[var(--gold)]" : "border-transparent"}`} />
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  <button type="submit" disabled={saving} data-testid="artwork-save-btn"
                    className="btn-gold w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2">
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />} Publish listing
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid sm:grid-cols-4 gap-6 mt-8">
          {[["Listings", works.length], ["Sales", sales.length],
            ["Pending payout", money(pending)], ["Paid out", money(payout)]].map(([l, v]) => (
            <div key={l} className="bg-white rounded-2xl border border-[var(--border-subtle)] p-6">
              <div className="text-xs text-[var(--text-muted)]">{l}</div>
              <div className="font-serif text-3xl mt-1">{v}</div>
            </div>
          ))}
        </div>

        {loading ? <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[var(--gold)]" /></div> : (
          <div className="grid lg:grid-cols-2 gap-10 mt-12">
            <div>
              <h2 className="font-serif text-2xl mb-5">Your listings</h2>
              <div className="space-y-3">
                {works.length === 0 && <p className="text-[var(--text-muted)] text-sm">No listings yet.</p>}
                {works.map((w) => (
                  <div key={w.id} className="bg-white rounded-xl border border-[var(--border-subtle)] p-4 flex gap-4 items-center" data-testid={`artist-work-${w.id}`}>
                    <img src={w.image_url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                    <div className="flex-1"><div className="font-medium">{w.title}</div><div className="text-xs text-[var(--text-muted)]">{money(w.price)}</div></div>
                    <span className={`text-[0.65rem] font-mono uppercase px-2.5 py-1 rounded-full ${w.status === "sold" ? "bg-stone-900 text-white" : w.status === "pending" ? "bg-amber-100 text-amber-800" : "escrow-badge"}`}>{w.status}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-serif text-2xl mb-5">Sales & payouts</h2>
              <div className="space-y-3">
                {sales.length === 0 && <p className="text-[var(--text-muted)] text-sm">No sales yet.</p>}
                {sales.map((s) => (
                  <div key={s.id} className="bg-white rounded-xl border border-[var(--border-subtle)] p-4" data-testid={`artist-sale-${s.id}`}>
                    <div className="flex justify-between items-start">
                      <div><div className="font-medium">{s.artwork_title}</div><div className="text-xs text-[var(--text-muted)]">Payout {money(s.artist_payout)} · fee {money(s.commission)}</div></div>
                      <span className="escrow-badge">{escrowLabel(s.escrow_status)}</span>
                    </div>
                    {s.escrow_status === "funds_held" && (
                      shipping === s.id ? (
                        <div className="flex gap-2 mt-3">
                          <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking #"
                            data-testid={`tracking-input-${s.id}`} className="flex-1 rounded-lg border border-[var(--border-subtle)] px-3 py-2 text-sm" />
                          <button onClick={() => ship(s.id)} data-testid={`ship-confirm-${s.id}`} className="btn-gold px-4 py-2 rounded-lg text-sm font-semibold">Ship</button>
                        </div>
                      ) : (
                        <button onClick={() => setShipping(s.id)} data-testid={`ship-btn-${s.id}`}
                          className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-[var(--gold)]"><Truck className="w-4 h-4" /> Mark as shipped</button>
                      )
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
